import React, { ReactNode, useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { useDebouncedValue } from '@apigrowfinance/pbolt-hooks';
import PageHeader from '@apigrowfinance/pbolt-page-header';
import SpotlightSearch, { ErrorType } from '@apigrowfinance/pbolt-spotlight-search';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import ChipFilter, { IChipFilter } from '@components/chipFilter/ChipFilter';
import programTypes from '@messages/programTypes';
import getAccountSearchQuery from '@pages/accountSearch/accountSearchQuery';
import getEnumTranslation from '@utils/getEnumTranslation';

export interface IResults {
    name: string;
    email: string;
    url: string;
    id: string;
    type?: ReactNode;
}

export type SearchType = 'name' | 'email' | 'document' | 'account';

const AccountSearch = () => {
    const { formatMessage } = useIntl();
    const { theme } = useTheme();
    const navigate = useNavigate();

    const [searchType, setSearchType] = useState<SearchType>('name');
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState<IResults[] | null>(null);

    const handleChipChange = (chipType: SearchType) => {
        setSearchType(chipType);
        setSearchResults(null);
    };

    const chipTypes: IChipFilter[] = [
        {
            label: formatMessage({
                id: `ChipFilter.name`,
                defaultMessage: 'Name',
            }),
            type: 'name',
        },
        {
            label: formatMessage({
                id: `ChipFilter.email`,
                defaultMessage: 'Email',
            }),
            type: 'email',
        },
        {
            label: formatMessage({
                id: `ChipFilter.document`,
                defaultMessage: 'Document',
            }),
            type: 'document',
        },
        {
            label: formatMessage({
                id: `ChipFilter.account`,
                defaultMessage: 'Account',
            }),
            type: 'account',
        },
    ];

    const getSearchLabel = () =>
        chipTypes.filter((chip) => chip.type === searchType)[0].label;

    const handleSearchTextChange = (updatedValue: string) => {
        setSearchText(updatedValue);
    };

    const handleOnClick = (url: string) => {
        navigate(url);
    };

    const [debouncedSearchValue] = useDebouncedValue(searchText, 1000);

    const {
        isFetching,
        data: accountData,
        isError,
        error,
        refetch: fetchAccountDetails,
    } = useQuery(
        ['accountSearch', debouncedSearchValue],
        () => getAccountSearchQuery(searchType, debouncedSearchValue),
        {
            refetchOnWindowFocus: false,
            enabled: false, // needed to handle refetch's manually
            retry: false,
        },
    );

    // Search when the user has entered an account number after debounce
    useEffect(() => {
        if (debouncedSearchValue.length === 0) {
            return;
        }
        fetchAccountDetails();
    }, [searchType, debouncedSearchValue, fetchAccountDetails]);

    // when accountData is returned create search elements
    useEffect(() => {
        if (accountData) {
            const modifiedArr = accountData.map(function (element) {
                return {
                    name: element.name,
                    email: element.email,
                    url: `/${element.account_id}/overview`,
                    id: `${element.account_id}`,
                    ...(element.program_type && {
                        type: getEnumTranslation(element.program_type, {
                            messages: programTypes,
                        }),
                    }),
                };
            });
            setSearchResults(modifiedArr);
        } else {
            setSearchResults(null);
        }
    }, [accountData]);

    return (
        <>
            <Box sx={{ width: '100%', flexGrow: 1 }}>
                <PageHeader
                    title={
                        <Typography
                            sx={{
                                color:
                                    theme?.custom?.brand?.textMain ||
                                    'text.primary',
                            }}
                            variant="h2"
                            component="h1"
                            data-testid="AccountSearch-heading">
                            <FormattedMessage
                                id="AccountSearch.heading"
                                defaultMessage="Accounts"
                            />
                        </Typography>
                    }
                    description={
                        <Typography
                            variant="subtitle2"
                            component="div"
                            data-testid="AccountSearch-caption">
                            <FormattedMessage
                                id="AccountSearch.caption"
                                defaultMessage="Search for all bank accounts under your organisation"
                            />
                        </Typography>
                    }
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mt: {
                        xs: 10,
                        sm: 10,
                        md: 23,
                    },
                }}>
                <Typography
                    sx={{
                        color: theme?.custom?.brand?.textMain || 'text.primary',
                        textAlign: 'center',
                    }}
                    variant="h3"
                    component="h1"
                    data-testid="AccountSearch-searchPrompt">
                    <FormattedMessage
                        id="AccountSearch.searchPrompt"
                        defaultMessage="How would you like to search?"
                    />
                </Typography>
                <Box
                    sx={{ mt: 3 }}
                    width={{
                        xs: theme.spacing(30),
                        sm: theme.spacing(50),
                        md: theme.spacing(70),
                    }}>
                    <ChipFilter
                        chipTypes={chipTypes}
                        onClick={handleChipChange}
                    />
                </Box>
                <Box
                    sx={{ mt: 6.25 }}
                    width={{
                        xs: theme.spacing(30),
                        sm: theme.spacing(50),
                        md: theme.spacing(70),
                    }}>
                    <SpotlightSearch
                        text={searchText}
                        handleChange={handleSearchTextChange}
                        entries={searchResults}
                        handleOnClick={handleOnClick}
                        isLoading={isFetching}
                        isError={isError}
                        error={error as ErrorType}
                        buttonCaption={formatMessage({
                            id: 'AccountSearch.searchCaption',
                            defaultMessage: 'Search',
                        })}
                        placeHolder={formatMessage(
                            {
                                id: 'AccountSearch.label',
                                defaultMessage: 'Search by {searchTypeLabel}',
                            },
                            { searchTypeLabel: getSearchLabel() },
                        )}
                        notfoundTitle={formatMessage({
                            id: 'AccountSearch.notfound',
                            defaultMessage: 'No customer account found',
                        })}
                        notfoundSubtitle={formatMessage({
                            id: 'AccountSearch.notfoundSubtitle',
                            defaultMessage:
                                'Please try searching for another account',
                        })}
                        errorTitle={formatMessage({
                            id: 'AccountSearch.errorMessage',
                            defaultMessage: 'An error occurred while searching',
                        })}
                    />
                </Box>
            </Box>
        </>
    );
};

export default AccountSearch;
